import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { useFocusMode } from '@/hooks/useFocusMode';
import { cn } from '@/lib/utils';
import { useWindowManager } from '@/contexts/WindowManager';
import { useToast } from '@/hooks/use-toast';

interface FocusModeSelectorProps {
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Dropdown selector for focus modes
 * Features: Quick toggle, duration picker, visual indicators, template creation
 */
export const FocusModeSelector: React.FC<FocusModeSelectorProps> = ({
  isOpen,
  onToggle,
}) => {
  const {
    focusModes,
    activeFocusMode,
    isActive,
    activate,
    deactivate,
    createFromTemplate,
    getTemplates,
    loading,
  } = useFocusMode();
  const { openWindow } = useWindowManager();
  const { toast } = useToast();

  const [selectedModeId, setSelectedModeId] = useState<string | null>(null);
  const [showDurationPicker, setShowDurationPicker] = useState(false);

  const templates = getTemplates();

  // Duration options (in minutes)
  const durations = [
    { label: '1 hour', value: 60 },
    { label: '2 hours', value: 120 },
    { label: '4 hours', value: 240 },
    { label: 'Until evening (6 PM)', value: getMinutesUntil(18) },
    { label: 'Until tomorrow', value: getMinutesUntil(9, 1) },
    { label: 'Indefinitely', value: undefined },
  ];

  // Calculate minutes until a specific hour
  function getMinutesUntil(targetHour: number, daysOffset: number = 0): number {
    const now = new Date();
    const target = new Date();
    target.setHours(targetHour, 0, 0, 0);
    target.setDate(target.getDate() + daysOffset);
    
    if (target < now && daysOffset === 0) {
      target.setDate(target.getDate() + 1);
    }
    
    return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60));
  }

  // Get icon component for a mode
  const getModeIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName] || Icons.Moon;
    return IconComponent;
  };

  // Handle mode activation
  const handleActivate = async (modeId: string, duration?: number) => {
    try {
      await activate(modeId, duration);
      setShowDurationPicker(false);
      setSelectedModeId(null);
      
      const mode = focusModes.find(m => m.id === modeId);
      toast({
        title: 'Focus Mode Activated',
        description: `${mode?.name || 'Focus mode'} is now active${duration ? ` for ${duration} minutes` : ''}`,
      });
    } catch (error) {
      console.error('Failed to activate focus mode:', error);
      toast({
        title: 'Activation Failed',
        description: error instanceof Error ? error.message : 'Failed to activate focus mode',
        variant: 'destructive',
      });
    }
  };

  // Handle mode deactivation
  const handleDeactivate = async () => {
    try {
      const modeName = activeFocusMode?.name;
      await deactivate();
      toast({
        title: 'Focus Mode Deactivated',
        description: `${modeName || 'Focus mode'} has been turned off`,
      });
    } catch (error) {
      console.error('Failed to deactivate focus mode:', error);
      toast({
        title: 'Deactivation Failed',
        description: error instanceof Error ? error.message : 'Failed to deactivate focus mode',
        variant: 'destructive',
      });
    }
  };

  // Handle template creation
  const handleCreateFromTemplate = async (templateName: string) => {
    try {
      const newMode = await createFromTemplate(templateName);
      if (newMode) {
        toast({
          title: 'Focus Mode Created',
          description: `${templateName} has been added to your focus modes`,
        });
        // Automatically activate it for 1 hour
        await handleActivate(newMode.id, 60);
      }
    } catch (error) {
      console.error('Failed to create focus mode from template:', error);
      toast({
        title: 'Creation Failed',
        description: error instanceof Error ? error.message : 'Failed to create focus mode',
        variant: 'destructive',
      });
    }
  };

  // Show duration picker for a mode
  const showDurationFor = (modeId: string) => {
    setSelectedModeId(modeId);
    setShowDurationPicker(true);
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={onToggle}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200',
          'backdrop-blur-xl border',
          isActive 
            ? 'bg-purple-600/20 border-purple-500/50 text-purple-300'
            : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20'
        )}
      >
        {isActive && activeFocusMode ? (
          <>
            {React.createElement(getModeIcon(activeFocusMode.icon_name), { className: 'w-4 h-4' })}
            <span className="text-sm font-medium">{activeFocusMode.name}</span>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 rounded-full bg-purple-400"
            />
          </>
        ) : (
          <>
            <Icons.Moon className="w-4 h-4" />
            <span className="text-sm">Focus</span>
          </>
        )}
        <Icons.ChevronDown className={cn(
          'w-3 h-3 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'absolute top-full right-0 mt-2 w-80 z-50',
              'rounded-xl overflow-hidden',
              'backdrop-blur-xl bg-gray-900/95 border border-white/10',
              'shadow-[0_20px_60px_rgba(0,0,0,0.5)]'
            )}
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white mb-1">Focus Modes</h3>
              <p className="text-xs text-white/60">Control which notifications you receive</p>
            </div>

            {/* Active Mode (if any) */}
            {isActive && activeFocusMode && (
              <div className="p-3 bg-purple-500/10 border-b border-purple-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${activeFocusMode.color}20` }}
                    >
                      {React.createElement(getModeIcon(activeFocusMode.icon_name), { 
                        className: 'w-5 h-5',
                        style: { color: activeFocusMode.color }
                      })}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{activeFocusMode.name}</p>
                      <p className="text-xs text-white/60">Currently active</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleDeactivate}
                    className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors"
                  >
                    Turn Off
                  </button>
                </div>
              </div>
            )}

            {/* Duration Picker */}
            {showDurationPicker && selectedModeId && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="border-b border-white/10 overflow-hidden"
              >
                <div className="p-3 bg-white/5">
                  <p className="text-xs font-medium text-white/80 mb-2">Activate for:</p>
                  <div className="space-y-1">
                    {durations.map((duration, index) => (
                      <button
                        key={index}
                        onClick={() => handleActivate(selectedModeId, duration.value)}
                        className="w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs transition-colors"
                      >
                        {duration.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setShowDurationPicker(false);
                      setSelectedModeId(null);
                    }}
                    className="w-full mt-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 text-xs transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}

            {/* Available Modes */}
            <div className="max-h-80 overflow-y-auto">
              {/* User's Custom Modes */}
              {focusModes.length > 0 && (
                <div className="p-2">
                  <p className="text-xs font-medium text-white/50 px-2 py-1 mb-1">Your Modes</p>
                  {focusModes.map((mode) => {
                    const ModeIcon = getModeIcon(mode.icon_name);
                    const isCurrentlyActive = isActive && activeFocusMode?.id === mode.id;

                    return (
                      <button
                        key={mode.id}
                        onClick={() => !isCurrentlyActive && showDurationFor(mode.id)}
                        disabled={isCurrentlyActive}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                          isCurrentlyActive 
                            ? 'bg-purple-500/20 cursor-not-allowed'
                            : 'hover:bg-white/5 cursor-pointer'
                        )}
                      >
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${mode.color}20` }}
                        >
                          <ModeIcon 
                            className="w-4 h-4"
                            style={{ color: mode.color }}
                          />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-white">{mode.name}</p>
                          {mode.do_not_disturb && (
                            <p className="text-xs text-white/60">Do Not Disturb</p>
                          )}
                        </div>
                        {isCurrentlyActive && (
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: mode.color }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Templates (if user has no modes yet) */}
              {focusModes.length === 0 && !loading && (
                <div className="p-2">
                  <p className="text-xs font-medium text-white/50 px-2 py-1 mb-1">Get Started</p>
                  {templates.map((template, index) => {
                    const TemplateIcon = getModeIcon(template.icon_name);

                    return (
                      <button
                        key={index}
                        onClick={() => handleCreateFromTemplate(template.name)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${template.color}20` }}
                        >
                          <TemplateIcon 
                            className="w-4 h-4"
                            style={{ color: template.color }}
                          />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-white">{template.name}</p>
                          <p className="text-xs text-white/60">{template.description}</p>
                        </div>
                        <Icons.Plus className="w-4 h-4 text-white/40" />
                      </button>
                    );
                  })}
                </div>
              )}

              {loading && (
                <div className="p-8 text-center text-white/50 text-sm">
                  Loading focus modes...
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-white/10 bg-white/5">
              <button
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-medium transition-colors"
                onClick={() => {
                  onToggle(); // Close the dropdown
                  openWindow(
                    'notification-settings',
                    'Notification Settings',
                    React.createElement(
                      React.lazy(() => import('@/components/windows/NotificationSettingsWindow')),
                      {}
                    ),
                    {
                      size: { width: 900, height: 700 },
                      position: { x: 100, y: 100 },
                    }
                  );
                }}
              >
                <Icons.Settings className="w-3 h-3" />
                Manage Focus Modes
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
